import { Card, CardContent, Typography, Box, Chip } from '@mui/material'
import { Email, Phone } from '@mui/icons-material'

export interface InfoCardWidgetProps {
  title: string
  subtitle?: string
  description?: string
  email?: string
  phone?: string
  status?: 'active' | 'inactive' | 'pending'
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error'
  variant?: 'elevation' | 'outlined'
  size?: 'small' | 'medium' | 'large'
}

const InfoCardWidget = ({
  title,
  subtitle,
  description,
  email,
  phone,
  status = 'active',
  color = 'primary',
  variant = 'elevation',
  size = 'medium'
}: InfoCardWidgetProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'error'
      case 'pending':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getSizeProps = (size: string) => {
    switch (size) {
      case 'small':
        return { padding: 1, minHeight: 120 }
      case 'large':
        return { padding: 3, minHeight: 200 }
      default:
        return { padding: 2, minHeight: 160 }
    }
  }

  return (
    <Card 
      variant={variant} 
      elevation={variant === 'elevation' ? 3 : 0}
      sx={{ 
        maxWidth: 400,
        ...getSizeProps(size),
        border: variant === 'outlined' ? 1 : 0,
        borderColor: `${color}.main`,
        '&:hover': {
          transform: 'translateY(-2px)',
          transition: 'transform 0.2s ease-in-out'
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography 
            variant={size === 'large' ? 'h5' : size === 'small' ? 'h6' : 'h6'} 
            component="h2" 
            color={`${color}.main`}
            gutterBottom
          >
            {title}
          </Typography>
          <Chip 
            label={status} 
            color={getStatusColor(status) as any}
            size="small"
          />
        </Box>
        
        {subtitle && (
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {subtitle}
          </Typography>
        )}
        
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
            </Box>
          )}
          
          {phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {phone}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default InfoCardWidget
